<template>
  <div class="custom-states-panel">
    <div
      v-for="info in localListInfo"
      :key="info.scope"
    >
      <span>{{ info.title }}</span>
      <md-content class="tags md-scrollbar">
        <draggable
          v-model="info.list"
          :group="{ name: 'scopes', pull: true, put: true }"
          item-key="id"
          :disabled="readonly"
          class="custom-enum-state-grid"
          @start="onDragStart"
          @end="onDragEnd"
          @add="onAdd($event, info)"
        >
          <custom-enum-state-row
            v-for="customEnumState in info.list"
            :key="customEnumState.id"
            :case-conf="caseConf"
            :custom-enum-state="customEnumState"
            :readonly="readonly"
            @apply="customEnumState => applyRow(customEnumState, info.list)"
            @update="customEnumState => updateRow(customEnumState)"
            @remove="customEnumState => removeRow(customEnumState)"
          />
          <div v-if="!info.list.length" slot="footer" key="empty" class="empty-placeholder">
            No items
          </div>
        </draggable>
      </md-content>
    </div>
    <el-popover
      v-model="newPopoverVisible"
      placement="left"
      trigger="click"
    >
      <custom-enum-state-editor
        :case-conf="caseConf"
        :custom-enum-state="state.newCustomEnumState"
        :readonly="readonly"
        apply-label="Create"
        @apply="applyNew"
        @cancel="cancelNew"
      />
      <el-button slot="reference" size="small" icon="plus" />
    </el-popover>
    <md-dialog-alert
      :md-active.sync="state.conflict"
      md-title="Already exists"
      md-content="There is already a custom state with the name you entered."
    />
  </div>
</template>

<script>
import Chance from 'chance';
import { Action } from 'rete-history-plugin';
import cloneDeep from 'lodash/cloneDeep';
import without from 'lodash/without';

import Draggable from 'vuedraggable';
import CustomEnumStateRow from './CustomEnumStateRow.vue';
import CustomEnumStateEditor from './CustomEnumStateEditor.vue';
import scopeMixins from '../utils/ScopeMixins';
import { activeNpcs } from '~lib/common/utils/npc';
import { patientLabel, npcLabel } from '~lib/common/utils/labels';

const chance = new Chance();

export default {
  name: 'custom-enum-states-panel',
  components: {
    Draggable,
    CustomEnumStateRow,
    CustomEnumStateEditor
  },
  mixins: [ scopeMixins ],
  props: {
    caseConf: {
      type: Object,
      default: () => {}
    },
    customEnumStates: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      required: true,
    },
    emitter: {
      type: Object,
      default: () => {}
    },
  },
  data() {
    return {
      state: {
        newCustomEnumState: this.createEnum(),
        conflict: false,
      },
      newPopoverVisible: false,
      isDragging: false,
      localListInfo: [],
    };
  },
  computed: {
    npcSyncSignature() {
      // This lets us see if the names or keys of any npc changed in an efficient way
      return this.caseConf.npcs
        .map(npc => `${npc.name}|${npc.key}`)
        .sort()
        .join('||');
    },
  },
  watch: {
    customEnumStates(newVal, oldVal) {
      if (this.isDragging) return;
      this.syncState();
      if (this.disableHistoryTrackingOnNextProcess) {
        this.disableHistoryTrackingOnNextProcess = false;
        return;
      }
      this.emitter.trigger(
        'addhistory',
        new CustomStatesChangeAction(this, oldVal, newVal),
      );
    },
    npcSyncSignature(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.syncState();
      }
    },
  },
  mounted() {
    this.syncState();
  },
  methods: {
    syncState() {
      if (!this.caseConf) {
        return;
      }
      this.localListInfo =
      [
        {
          title: 'Scenario',
          scope: 'CASE',
          list: this.customEnumStates.filter(state => state.scope === 'CASE'),
        },
        {
          title: patientLabel(this.caseConf),
          scope: 'PATIENT',
          list: this.customEnumStates.filter(state => state.scope === 'PATIENT'),
        }
      ];
      for (const npc of activeNpcs(this.caseConf)) {
        this.localListInfo.push({
          title: npcLabel(npc),
          scope: npc.key,
          list: this.customEnumStates.filter(state => state.scope === npc.key),
        });
      }
    },
    onDragStart() {
      this.isDragging = true;
    },
    onDragEnd () {
      this.isDragging = false;
      this.$nextTick(() => {
        const updateList = this.localListInfo.flatMap(info => info.list.map(item => ({ ...item, scope: info.scope})));
        this.$emit('update', updateList);
      });
    },
    onAdd(evt, listInfo) {
      var moved = listInfo.list[evt.newIndex];
      if (moved) moved.scope = listInfo.scope;
    },
    applyNew(newCustomEnumState) {
      if (!newCustomEnumState.label) {
        return;
      }
      this.newPopoverVisible = false;
      if (this.customEnumStates.find(state => state.label === newCustomEnumState.label)) {
        this.state.conflict = true;
        return;
      }
      this.$emit('update', this.customEnumStates.concat(newCustomEnumState));
      this.state.newCustomEnumState = this.createEnum();
    },
    cancelNew() {
      this.newPopoverVisible = false;
      this.state.newCustomEnumState = this.createEnum();
    },
    applyRow(customEnumState, localList) {
      const index = localList.findIndex(state => state.id === customEnumState.id);
      if (index >= 0) {
        this.$set(localList, index, customEnumState); // is $set needed?
        this.update();
      }
    },
    updateRow() {
      this.update();
    },
    removeRow(customEnumState) {
      this.update(customEnumState);
    },
    update(stateToRemove) {
      const updateList = this.localListInfo.flatMap(info => info.list);
      if (stateToRemove) {
        this.$emit('update', without(updateList, stateToRemove));
      } else {
        this.$emit('update', updateList);
      }
    },
    createEnum() {
      return {
        id: chance.guid(),
        label: '',
        scope: 'CASE',
        values: [],
      };
    },
  },
};

class CustomStatesChangeAction extends Action {
  constructor(panelComponentInstance, oldCustomStates, newCustomStates) {
    super();
    this.panelComponentInstance = panelComponentInstance;
    this.oldCustomStates = cloneDeep(oldCustomStates);
    this.newCustomStates = cloneDeep(newCustomStates);
  }

  undo() {
    this.panelComponentInstance.disableHistoryTrackingOnNextProcess = true;
    this.panelComponentInstance.$emit('update', this.oldCustomStates);
  }

  redo() {
    this.panelComponentInstance.disableHistoryTrackingOnNextProcess = true;
    this.panelComponentInstance.$emit('update', this.newCustomStates);
  }
}
</script>

<style scoped>
.custom-states-panel {
  min-width: 300px;
  max-width: 500px;
}

.tags {
  overflow: auto;
  height: calc(100% - 64px);
  background-color: #303143;
}

.custom-enum-state-grid {
  margin-bottom: 4px;
}

.empty-placeholder {
  color: white;
  padding: 0 12px 8px 12px;
  opacity: 0.6;
  user-select: none;
}
</style>
